import { useState, type FormEvent } from 'react';
import { useSubscribeNewsletter } from '../api/queries';
import { ApiError } from '../api/client';

interface NewsletterData {
  heading?: string;
  body?: string;
  consentText?: string;
  buttonLabel?: string;
}

/**
 * Newsletter sign-up. Consent is an explicit, separate checkbox and the exact wording is
 * sent to the API, which records it against the subscription — marketing consent is never
 * implied by using the site or by making a booking.
 */
export function NewsletterBlock({ data }: { data: NewsletterData }) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  const subscribe = useSubscribeNewsletter();

  const consentText =
    data.consentText ?? 'I would like to receive news, events and seasonal menus from SAZERAC.';

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!email.trim() || !consent) return;
    subscribe.mutate({ email: email.trim(), consentGiven: consent, source: 'website-footer' });
  };

  const consentError = touched && !consent ? 'Please confirm you would like to hear from us.' : undefined;

  return (
    <section className="sz-newsletter">
      <h3 className="sz-newsletter__heading">{data.heading ?? 'Stay in the know'}</h3>
      {data.body && <p className="sz-newsletter__body">{data.body}</p>}

      {subscribe.isSuccess ? (
        <p role="status" className="sz-newsletter__success">
          {subscribe.data?.detail ?? "Thank you — you're on the list."}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="sz-newsletter__form" noValidate>
          <div className="sz-newsletter__row">
            <label htmlFor="newsletter-email" className="sz-visually-hidden">
              Your email address
            </label>
            <input
              id="newsletter-email"
              type="email"
              required
              autoComplete="email"
              placeholder="Your email"
              className="sz-newsletter__input"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-describedby="newsletter-consent-label"
            />
            <button type="submit" className="sz-btn sz-btn--gold" disabled={subscribe.isPending}>
              {subscribe.isPending ? 'Signing up…' : (data.buttonLabel ?? 'Sign Up')}
            </button>
          </div>

          <label className="sz-checkbox sz-newsletter__consent" id="newsletter-consent-label">
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              aria-invalid={Boolean(consentError)}
            />
            <span>{consentText}</span>
          </label>

          {consentError && (
            <p className="sz-field-error" role="alert">
              {consentError}
            </p>
          )}

          {subscribe.isError && (
            <p className="sz-field-error" role="alert">
              {subscribe.error instanceof ApiError
                ? subscribe.error.message
                : 'We could not sign you up just now. Please try again.'}
            </p>
          )}

          <p className="sz-newsletter__small">
            You can unsubscribe at any time. Unsubscribing never affects messages about your bookings.
          </p>
        </form>
      )}
    </section>
  );
}
